import { Chart, Circle, HelpText } from '@mm/components';
import { landmineNames } from '@mm/constants/landmine-names';
import { DownloadIcon } from '@mm/icons';
import { useBotAnalyticsCRUDActions } from '@mm/state/actions/BotAnalyticsCRUDActions';
import {
  BotAgentConversationsType,
  BotAgentMessagesType,
} from '@mm/state/interfaces/botAnalytics';
import {
  openUniversalUpgradeLandmineModal,
  useModalActions,
} from '@mm/state/modules/modalsState/actions';
import {
  selectBotAgentConversations,
  selectBotAgentMessages,
} from '@mm/state/selectors/botAnalyticsSelector';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import classnames from 'classnames';
import { map, sum } from 'lodash';
import { Moment } from 'moment';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { colors } from '../../../../../styles/colors';
import { getDormatedDateLabel } from '../../../../../utils/dateUtils';
import './BotAgentOutgoingChart.scss';

type Props = {
  startDate: Moment | null;
  endDate: Moment | null;
  dateRangeMode: string | null;
};

export const BotAgentOutgoingChart = (props: Props) => {
  const { startDate, endDate, dateRangeMode } = props;
  const activeChatbotId = useSelector(activeChatbotIdSelector);
  const [isBotAgentMessagesMode, setIsBotAgentMessagesMode] = useState(false);
  const botAgentMessages = useSelector(selectBotAgentMessages);
  const botAgentConversations = useSelector(selectBotAgentConversations);
  const { messages, dates: messageDates } = botAgentMessages;
  const { conversations, dates: conversationsDates } = botAgentConversations;
  const { retrieveBotAgentOutgoingAnalytics, exportConversationsStats } =
    useBotAnalyticsCRUDActions();
  const { openExportModal } = useModalActions();
  const featureFlags = useSelector(combinedFeatureFlagsSelector);
  const canExportConversationsAnalytics = featureFlags.analyticsConversations;

  useEffect(() => {
    retrieveBotAgentOutgoingAnalytics(
      activeChatbotId,
      isBotAgentMessagesMode ? undefined : 'by_conversation',
      startDate?.format('YYYY-MM-DD'),
      endDate?.format('YYYY-MM-DD'),
    );
  }, [startDate, endDate, isBotAgentMessagesMode]);

  const title = isBotAgentMessagesMode
    ? ['Bot messages', 'Agent messages']
    : ['Bot conversations', 'Agent conversations'];

  const data = isBotAgentMessagesMode ? messages : conversations;
  const dates = isBotAgentMessagesMode ? messageDates : conversationsDates;
  const agent = isBotAgentMessagesMode
    ? map(data as BotAgentMessagesType['messages'], 'agent')
    : map(data as BotAgentConversationsType['conversations'], 'mmLivechat');
  const bot = isBotAgentMessagesMode
    ? map(data as BotAgentMessagesType['messages'], 'bot')
    : map(data as BotAgentConversationsType['conversations'], 'mmBot');
  const total = (sum(agent) ?? 0) + (sum(bot) ?? 0);

  const chartData = {
    labels: getDormatedDateLabel(dates, 'day'),
    data: [bot, agent],
    color: [colors.skyBlue, colors.orangeLighter],
    title,
    types: ['bar', 'bar'],
    withoutGradient: true,
    stack: true,
  };

  const additionalOpts = {
    stacked: true,
  };

  const toggleBotAgentConversations = classnames(
    'bot-agent-outgoing-chart__header-title-toggle',
    {
      'bot-agent-outgoing-chart__header-title-toggle--active':
        !isBotAgentMessagesMode,
    },
  );
  const toggleBotAgentMessages = classnames(
    'bot-agent-outgoing-chart__header-title-toggle',
    {
      'bot-agent-outgoing-chart__header-title-toggle--active':
        isBotAgentMessagesMode,
    },
  );

  const showUpgradeModal = (feature: keyof typeof featureFlags) => {
    openUniversalUpgradeLandmineModal({
      landmineName: landmineNames['CONVERSATION-ANALYTICS-EXPORT'],
      feature,
    });
  };

  const onDownloadIconClick = () => {
    if (!canExportConversationsAnalytics) {
      showUpgradeModal('analyticsConversations');
    } else {
      const description =
        `We’ll prepare your ${
          isBotAgentMessagesMode
            ? 'outgoing messages statistics'
            : 'conversations statistics'
        } export and email you when it’s ready. ` +
        'Enter the email address you’d like us to send your download link to.';

      openExportModal({
        title: isBotAgentMessagesMode
          ? 'Export outgoing messages statistics'
          : 'Export conversations statistics',
        description,
        onExport: (email: string) => {
          return exportConversationsStats(
            activeChatbotId,
            email,
            isBotAgentMessagesMode ? undefined : 'by_conversation',
            startDate?.format('YYYY-MM-DD'),
            endDate?.format('YYYY-MM-DD'),
          );
        },
      });
    }
  };

  const customLegend = (
    <>
      <div className="bot-agent-outgoing-chart__legend">
        {chartData?.title?.map((title, i) => (
          <div className="c-chart__legend-item-container" key={title}>
            <div className="c-chart__legend-item">
              <Circle
                className="c-chart__legend-indicator"
                color={chartData?.color[i]}
              />
              <HelpText className="c-chart__legend-label" text={title} />
            </div>
            <div className="c-chart__legend-item-amount">
              {sum(chartData.data[i]) ?? 0}
            </div>
          </div>
        ))}
      </div>
      <div className="bot-agent-outgoing-chart__legend-info">
        <div className="bot-agent-outgoing-chart__legend-date">
          <span className="bot-agent-outgoing-chart__legend-date-show">
            Show:{' '}
          </span>
          {dateRangeMode ? dateRangeMode : 'Custom date range'}
        </div>
        <div className="bot-agent-outgoing-chart__total">Total: {total}</div>
      </div>
    </>
  );

  return (
    <div className="bot-agent-outgoing-chart">
      <div className="bot-agent-outgoing-chart__header">
        <div
          className={toggleBotAgentMessages}
          onClick={() => setIsBotAgentMessagesMode(true)}
        >
          Outgoing Messages
        </div>
        <div
          className={toggleBotAgentConversations}
          onClick={() => setIsBotAgentMessagesMode(false)}
        >
          Conversations
        </div>
        <div
          className="keywords-triggered__title-upload-icon"
          onClick={onDownloadIconClick}
        >
          <DownloadIcon />
        </div>
      </div>
      <Chart
        className="contacts-conversations__chart-main"
        data={chartData}
        customLegend={customLegend}
        additionalOpts={additionalOpts}
      />
    </div>
  );
};
